
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ArticleElements } from '@/components/features/academy/article-elements';
import { ArticleIndex } from '@/components/features/academy/article-index';
import { BaseLayout } from '@/components/templates';
import { withUrql } from '@/config/graphql';
import { IndexHeading } from '@/types/academy';
import { getArticleUrl, getIndexHeadings } from '@/utils/academy';
import { Box, Container, Grid, GridItem, Heading, Text, useBreakpointValue, } from '@chakra-ui/react';
import { contentfulClient } from '@cms/client';
import { ArticleCard, Breadcrumbs, Carousel } from '@meritcircle/ui/components';
import { GetStaticProps, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { ArticlePageDocument, ArticlePageQueryVariables } from '@cms/graphql/articlePage.generated';
import { PageAcademyArticle, PageAcademyArticleCollection } from '@cms/graphql/types.generated';
import { deletedEntries } from '@/utils/contentful';
import { URLS } from '@/config/urls';
import { useRouter } from 'next/router';
import { canonicalUrl as getCanonicalUrl } from '@/utils/seo';
import { Seo } from '@/components/utils/seo/seo-component';
import { formatReadingTime } from '@/components/features/academy/academy.helpers';
import { getOpenGraphProps } from '@/components/utils/seo/get-open-graph-props';
import { GuideJsonLd } from '@/components/utils/seo/guide-json-ld';
import { BreadcrumbJsonLd } from 'next-seo';
type PageProps = {
    article: PageAcademyArticle;
    slug: string;
    headings: IndexHeading[];
    lastUpdate: string;
};
const Page: NextPage<PageProps> = ({ article, slug, headings, lastUpdate }) => {
    const { t } = useTranslation('academy');
    const router = useRouter();
    const slidesPerView = useBreakpointValue({ base: 2, md: 3.2, xl: 4.2 });
    const slidesPerGroup = useBreakpointValue({ base: 2, md: 3, xl: 4 });
    const relatedArticles = article?.relatedCollection?.items.filter(deletedEntries);
    const breadcrumbs = [
        { title: t('title'), href: URLS.ACADEMY },
        { title: article.title, href: getArticleUrl(slug) },
    ];
    const { linkedSeoTags, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords } = article;
    const canonicalUrl = getCanonicalUrl(router);
    return (<BaseLayout>
      <Seo title={linkedSeoTags?.title || metaTitle || article.title} description={linkedSeoTags?.description || metaDescription || article.overviewDescription} nofollow={linkedSeoTags?.noFollow || noFollow} noindex={linkedSeoTags?.noIndex || noIndex} canonical={canonicalUrl} keywords={linkedSeoTags?.keywords || keywords} lastUpdate={lastUpdate} openGraph={getOpenGraphProps({
            title: linkedSeoTags?.title || metaTitle || article.title,
            description: linkedSeoTags?.description || metaDescription || article.overviewDescription,
            url: canonicalUrl,
            image: ogImage
        })}/>
      <BreadcrumbJsonLd itemListElements={breadcrumbs.map((breadcrumb, idx) => ({
            position: idx + 1,
            name: breadcrumb.title,
            item: breadcrumb.href
        }))}/>
      <GuideJsonLd title={article.title} summary={article.intro} firstPublishedAt={article.sys?.firstPublishedAt} publishedAt={article.sys?.publishedAt}/>
      <Container position="relative">
        <Breadcrumbs breadcrumbs={breadcrumbs} mt={6} mb={{ base: 10, md: 20 }}/>
        <Grid templateColumns={{ base: 'repeat(4, 1fr)', md: 'repeat(16, 1fr)' }} gap={0} minH="100vh">
          <GridItem display={{ base: 'none', md: 'block' }} colSpan={4}>
            <ArticleIndex title={t('article.index.title')} headings={headings} top={112}/>
          </GridItem>
          <GridItem colSpan={{ base: 4, md: 12 }}>
            <Box px={{ base: 5, md: '5xl' }}>
              <Heading as="h1" size="2xl" variant="glowing" color="lightPurple" mb={4}>
                {article.title}
              </Heading>
              {article.readingTime && (<Text variant="muted" textTransform="uppercase" mb={8}>
                  {formatReadingTime(article.readingTime, t('common:date.minute'), t('common:date.minutes'))}
                </Text>)}
              <Text as="p" variant="muted" mt={{ base: 'md', md: 'lg' }}>
                {article.intro}
              </Text>
              <ArticleElements elements={article.contentCollection.items}/>
            </Box>
          </GridItem>
        </Grid>
      </Container>
      {relatedArticles.length > 0 && (<Box py={{ base: '4xl', md: '5xl' }} borderTop="1px solid" borderColor="borderLight">
          <Carousel slidesPerView={slidesPerView} slidesPerGroup={slidesPerGroup} title={t('article.related.title')}>
            {relatedArticles.map(related => (<ArticleCard key={related.urlSlug} href={related.urlSlug} title={related.overviewTitle || related.title} text={related.overviewDescription} background={related.overviewImage?.url}/>))}
          </Carousel>
        </Box>)}
    </BaseLayout>);
};
export const getStaticPaths = async () => {
    return {
        /**
         * We don't build any CMS pages statically. However, next
         * requires you to at least add one param. Therefore this fake page is added,
         * which during build time already resolves to a 404 page. After that
         * all pages are rendered on the server and cached there. Every 30 mins (if the page is hit)
         * the server will revalidate the page.
         */
        paths: [{ params: { slug: 'slug' } }],
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps = async (ctx) => {
    const slug = `${ctx.params.slug}`;
    // Single article is fetched as a collection in order to use the 'slug' param instead of querying by id.
    const articleCollectionFromContentful = await contentfulClient
        .query<{
        pageAcademyArticleCollection: PageAcademyArticleCollection;
    }, ArticlePageQueryVariables>(ArticlePageDocument, {
        slug
    })
        .toPromise();
    // Nested IF because this way it's removed from prod build :)
    if (process.env.NODE_ENV === 'development') {
        if (articleCollectionFromContentful.error) {
            console.log('👻 Error during fetch from Contentful GQL');
            console.error(articleCollectionFromContentful.error);
        }
    }
    const article = articleCollectionFromContentful?.data?.pageAcademyArticleCollection?.items?.[0];
    if (!article) {
        console.log('👻 No article found for slug:', slug);
        return { notFound: true };
    }
    console.log('✨ Fetched article from contentful for slug:', slug, new Date());
    const headings = getIndexHeadings(article.contentCollection.items);
    return {
        props: {
            article,
            slug,
            headings,
            lastUpdate: `${new Date()}`
        },
        revalidate: 5 * 60
    };
};
export default withUrql(Page);

    async function __Next_Translate__getStaticProps__1973b3b286e__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/academy/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1973b3b286e__ as getStaticProps }
  